'use client';

import { useState } from 'react';
import { Check } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import LabelBadge from '@/components/LabelBadge';
import SectionTitle from '@/components/SectionTitle';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import FormInputWithLabel from '@/components/ui/form/FormInputWithLabel';
import FormSectionTitle from '@/components/ui/form/FormSectionTitle';

export default function DashboardPersonalPassword() {
    const { handleError, errorMessage, clearError } = useHandleError();
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState({ current_password: '', password: '' });
    const closeModal = useModalStore((state) => state.closeModal);
    const showToastAsync = useToastStore((s) => s.showToastAsync);

    const handleSubmit = async () => {
        try {
            clearError();
            setIsProcessing(true);
            await apiFetch(`/users/me/password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            closeModal();
            await showToastAsync('パスワードの設定が完了しました', 'success');
        } catch (err: unknown) {
            handleError(err);
            setIsProcessing(false);
        }
    };

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title={`Password`} />
                </div>
            </div>
            <div className="flex flex-col w-full">
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-4">
                    <FormSectionTitle title="パスワード" />
                </div>
                <div className="flex flex-col items-start justify-start w-full py-4">
                    <div className="flex items-start justify-start w-full">
                        <ErrorBox message={errorMessage?.message} />
                    </div>
                    <div id="current_password" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full">
                            <FormFieldLabel title="現在のパスワード" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="current_password" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="current_password"
                                name="current_password"
                                label="8～20文字の半角英数字混在"
                                type="password"
                                maxLength={20}
                                autoComplete="off"
                                value={formData.current_password}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        current_password: e.target.value,
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="password" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full">
                            <FormFieldLabel title="新しいパスワード" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="password" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="password"
                                name="password"
                                label="8～20文字の半角英数字混在"
                                type="password"
                                maxLength={20}
                                autoComplete="off"
                                value={formData.password}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        password: e.target.value,
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-start justify-start w-full py-2">
                <div className="flex flex-col-reverse md:flex-row justify-center w-full pt-12">
                    <div className="flex-grow"></div>
                    <div className="flex flex-col justify-end items-center">
                        <div className="flex justify-center items-center">
                            <ButtonGAdaptive
                                type="submit"
                                label="パスワードを更新する"
                                onClick={handleSubmit}
                                description="update password"
                                icon={Check}
                                isProcessing={isProcessing}
                            />
                        </div>
                    </div>
                    <div className="flex-grow"></div>
                </div>
            </div>
        </div>
    );
}
